import { create } from 'zustand';

/**
 * Light, dark, or whatever the operating system says. Dark until the user picks.
 *
 * The tokens for both themes live in `styles/_theme.scss` and are selected by
 * `data-theme` on <html>, so switching is one attribute write - nothing is
 * re-rendered to change colour. This store owns that attribute after start-up.
 * Before start-up, the inline script in index.html sets it from the same
 * storage key, which is what stops a dark-mode user seeing a white flash while
 * the bundle loads; the key below and the one in that script must agree.
 *
 * `resolved` is the theme actually showing, for the few things that cannot
 * read a CSS variable - a canvas chart picking its palette, the browser's
 * `theme-color`.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'cw-theme';

const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system'];

/* The browser chrome colour, per theme: the shell's top in each. */
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#1D1740', dark: '#0A0918' };

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
    return stored && THEME_MODES.includes(stored) ? stored : 'dark';
  } catch {
    return 'dark';
  }
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return systemPrefersDark() ? 'dark' : 'light';
  }

  return mode;
}

function apply(mode: ThemeMode, resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;

  // Suspends transitions for one frame, so a switch repaints at once instead
  // of every surface in the portal fading at its own speed.
  root.classList.add('theme-switching');
  root.dataset.theme = mode;
  root.dataset.resolvedTheme = resolved;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[resolved]);
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-switching')));
}

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const initialMode = readStoredMode();

export const useThemeStore = create<ThemeState>((set) => ({
  mode: initialMode,
  resolved: resolve(initialMode),

  setMode: (mode) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      /* the choice still applies for this session */
    }

    const resolved = resolve(mode);
    apply(mode, resolved);
    set({ mode, resolved });
  }
}));

/*
 * Follow the operating system while the user has asked to.
 *
 * Registered once, at module load. The CSS already follows the OS through the
 * media query, so this only keeps `resolved` - and `theme-color` - honest.
 */
if (typeof window !== 'undefined' && window.matchMedia) {
  apply(initialMode, resolve(initialMode));

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { mode } = useThemeStore.getState();

    if (mode === 'system') {
      const resolved = resolve(mode);
      apply(mode, resolved);
      useThemeStore.setState({ resolved });
    }
  });
}
